import React, { createContext, useContext, useEffect, useState } from 'react'
import axios from '@services/api/axiosConfig'
import { getEnv } from '@utils/env'
import { EmptyState } from '@enamelai/design-system'

export type TeamCatalog = {
  teams: string[]
  roleToTeam: Record<string, string>
  defaultTeam: string
  emptyRoleTeam: null
}

const TeamCatalogContext = createContext<TeamCatalog | null>(null)
let catalogRequest: Promise<TeamCatalog> | undefined

export const loadTeamCatalog = (): Promise<TeamCatalog> => {
  if (!catalogRequest) {
    catalogRequest = axios
      .get<{ data: TeamCatalog }>(
        `${getEnv('REACT_APP_BUSINESS_LAYER_PROVIDER')}/api/v1/provider/settings/teams`,
      )
      .then(({ data }) => {
        const catalog = data.data
        if (
          !Array.isArray(catalog?.teams) ||
          !catalog.roleToTeam ||
          !catalog.defaultTeam
        ) {
          throw new Error('Invalid team catalog')
        }
        return catalog
      })
      .catch((error) => {
        catalogRequest = undefined
        throw error
      })
  }
  return catalogRequest
}

export const useTeamCatalog = (): TeamCatalog => {
  const catalog = useContext(TeamCatalogContext)
  if (!catalog) throw new Error('Team catalog has not loaded')
  return catalog
}

/** Mount forms only after metadata is available so saving cannot use an empty fallback list. */
export function withTeamCatalog<P extends object>(
  Component: React.ComponentType<P>,
): React.FC<P> {
  const TeamCatalogBoundary: React.FC<P> = (props) => {
    const inherited = useContext(TeamCatalogContext)
    const [catalog, setCatalog] = useState<TeamCatalog | null>(null)
    const [error, setError] = useState(false)
    const [attempt, setAttempt] = useState(0)
    useEffect(() => {
      if (inherited) return
      let active = true
      setError(false)
      void loadTeamCatalog().then(
        (value) => {
          if (active) setCatalog(value)
        },
        () => {
          if (active) setError(true)
        },
      )
      return () => {
        active = false
      }
    }, [attempt, inherited])
    const value = inherited ?? catalog
    if (!value) return <EmptyState title="Unable to load teams" />
    return (
      <TeamCatalogContext.Provider value={value}>
        <Component {...props} />
      </TeamCatalogContext.Provider>
    )
  }
  TeamCatalogBoundary.displayName = `WithTeamCatalog(${Component.displayName ?? Component.name})`
  return TeamCatalogBoundary
}
